import {
  Box,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { AtterbergGeneral } from '../../../types/atterberg';
import {
  llActuationOptions,
  llMethodOptions,
  groovingToolOptions,
  samplePreparationOptions,
} from '../config/atterbergFields';
import { computePercentRetained425 } from './computations';
import { ObservationMediaPanel } from '../../../components/observations/ObservationMediaPanel';

interface Props {
  data: AtterbergGeneral;
  onChange: (field: keyof AtterbergGeneral, value: string | number | boolean | null) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

const inputSx = {
  '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.8rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.85rem' },
  '& .MuiInputBase-input::-webkit-outer-spin-button, & .MuiInputBase-input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none', margin: 0,
  },
  '& .MuiInputBase-input[type=number]': { MozAppearance: 'textfield' },
};

const unit = (s: string) => (
  <InputAdornment position="end">
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
      {s}
    </Typography>
  </InputAdornment>
);

export const AtterbergParametersTab = ({ data, onChange, readOnly = false, workOrderDetailKey }: Props) => {
  const theme = useTheme();
  const numVal = (key: keyof AtterbergGeneral) => {
    const v = data[key];
    return v === null || v === undefined ? '' : String(v);
  };
  const txtVal = (key: keyof AtterbergGeneral) => {
    const v = data[key];
    return v === null || v === undefined ? '' : String(v);
  };

  const handleNum = (key: keyof AtterbergGeneral, raw: string) =>
    onChange(key, raw === '' ? null : Number(raw));
  const handleText = (key: keyof AtterbergGeneral, raw: string) =>
    onChange(key, raw || null);

  const isPrepDisabled = data.samplePreparation === 'Natural' || data.samplePreparation === null;
  const pctRetained = computePercentRetained425(data.totalMassOfSample, data.massRetained425um);
  const pctPassing = pctRetained === null ? null : 100 - pctRetained;

  const sectionHeader = (s: string) => (
    <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.7rem', letterSpacing: 0.6 }}>
      {s}
    </Typography>
  );

  const cardSx = {
    p: 2, borderRadius: 2,
    border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Sample preparation block */}
      <Paper elevation={0} sx={cardSx}>
        {sectionHeader('Test Method Selection (ASTM D4318)')}
        <Box sx={{ mt: 1, mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', mb: 0.5 }}>
            Tested ……
          </Typography>
          <RadioGroup
            row
            value={data.samplePreparation ?? ''}
            onChange={(e) => onChange('samplePreparation', e.target.value || null)}
          >
            {samplePreparationOptions.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio size="small" disabled={readOnly} />}
                label={<Typography sx={{ fontSize: '0.78rem' }}>{opt.label}</Typography>}
              />
            ))}
          </RadioGroup>
        </Box>

        <Box sx={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, alignItems: 'center',
        }}>
          <TextField
            label="Total mass of sample"
            type="number"
            size="small"
            value={numVal('totalMassOfSample')}
            onChange={(e) => handleNum('totalMassOfSample', e.target.value)}
            InputProps={{ endAdornment: unit('g') }}
            sx={inputSx}
            disabled={readOnly}
            helperText=" "
            FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25, ml: 0 } }}
          />
          <TextField
            label="Mass +425µm sieve retained"
            type="number"
            size="small"
            value={numVal('massRetained425um')}
            onChange={(e) => handleNum('massRetained425um', e.target.value)}
            InputProps={{ endAdornment: unit('g') }}
            sx={inputSx}
            disabled={readOnly || isPrepDisabled}
            helperText={isPrepDisabled ? 'Select a preparation method' : ' '}
            FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25, ml: 0 } }}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>% Retained 425µm</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              {pctRetained === null ? '—' : `${pctRetained.toFixed(2)}%`}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>% Passing 425µm</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              {pctPassing === null ? '—' : `${pctPassing.toFixed(2)}%`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, mt: 1.5 }}>
          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={!!data.organic}
                onChange={(e) => onChange('organic', e.target.checked)}
                disabled={readOnly}
              />
            )}
            label={<Typography sx={{ fontSize: '0.78rem' }}>Organic</Typography>}
          />
        </Box>
      </Paper>

      {/* Two-column equipment block */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <Paper elevation={0} sx={cardSx}>
          {sectionHeader('Liquid Limit')}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mt: 1 }}>
            <TextField
              label="Single or Multipoint" select size="small"
              value={data.llMethod ?? 'Multipoint'}
              onChange={(e) => onChange('llMethod', e.target.value)}
              sx={inputSx} disabled={readOnly}
            >
              {llMethodOptions.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.8rem' }}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Mechanical or Manual" select size="small"
              value={data.llMechanicalOrManual ?? ''}
              onChange={(e) => onChange('llMechanicalOrManual', e.target.value || null)}
              sx={inputSx} disabled={readOnly}
            >
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>—</em></MenuItem>
              {llActuationOptions.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.8rem' }}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="LL Device No." size="small"
              value={txtVal('llDeviceNo')}
              onChange={(e) => handleText('llDeviceNo', e.target.value)}
              sx={inputSx} disabled={readOnly}
            />
            <TextField
              label="Oven No." size="small"
              value={txtVal('llOvenNo')}
              onChange={(e) => handleText('llOvenNo', e.target.value)}
              sx={inputSx} disabled={readOnly}
            />
            <TextField
              label="Oven temperature" type="number" size="small"
              value={numVal('llOvenTemperatureC')}
              onChange={(e) => handleNum('llOvenTemperatureC', e.target.value)}
              InputProps={{ endAdornment: unit('°C') }}
              sx={inputSx} disabled={readOnly}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          {sectionHeader('Plastic Limit')}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mt: 1 }}>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={!!data.plPerformedByHand}
                  onChange={(e) => onChange('plPerformedByHand', e.target.checked)}
                  disabled={readOnly}
                />
              )}
              label={<Typography sx={{ fontSize: '0.78rem' }}>Performed by hand</Typography>}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label="Grooving Tool Material" select size="small"
              value={data.plGroovingToolMaterial ?? ''}
              onChange={(e) => onChange('plGroovingToolMaterial', e.target.value || null)}
              sx={inputSx} disabled={readOnly}
            >
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>—</em></MenuItem>
              {groovingToolOptions.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.8rem' }}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Rolling device No." size="small"
              value={txtVal('plRollingDeviceNo')}
              onChange={(e) => handleText('plRollingDeviceNo', e.target.value)}
              sx={inputSx} disabled={readOnly || !!data.plPerformedByHand}
            />
            <TextField
              label="Oven No." size="small"
              value={txtVal('plOvenNo')}
              onChange={(e) => handleText('plOvenNo', e.target.value)}
              sx={inputSx} disabled={readOnly}
            />
            <TextField
              label="Oven temperature" type="number" size="small"
              value={numVal('plOvenTemperatureC')}
              onChange={(e) => handleNum('plOvenTemperatureC', e.target.value)}
              InputProps={{ endAdornment: unit('°C') }}
              sx={inputSx} disabled={readOnly}
            />
          </Box>
        </Paper>
      </Box>

      {/* Comments */}
      <Paper elevation={0} sx={cardSx}>
        {sectionHeader('Additional Observations (internal — not printed on the report)')}
        <TextField
          fullWidth multiline minRows={3} size="small"
          value={txtVal('comments')}
          onChange={(e) => handleText('comments', e.target.value)}
          sx={{ mt: 1, ...inputSx }}
          placeholder="Internal observations — for the lab and reviewer only."
          disabled={readOnly}
        />
        {workOrderDetailKey && (
          <ObservationMediaPanel workOrderDetailKey={workOrderDetailKey} readOnly={readOnly} />
        )}
      </Paper>
    </Box>
  );
};
